import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, User, Phone, Mail, Users, FileText, MapPin, RefreshCw } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  // obbligatori
  nome_referente: "",
  cognome_referente: "",
  telefono: "",
  consenso_privacy: false,
  // referente facoltativi
  email_referente: "",
  relazione_ospite: "",
  // ospite
  nome_ospite: "",
  eta_ospite: "",
  sesso_ospite: "",
  autosufficienza: "",
  urgenza_inserimento: "",
  tipo_posto: "",
  patologie: "",
  // info
  fonte: "",
  budget: "",
  zona: "",
  note: "",
};

function richiestaToForm(richiesta) {
  return {
    nome_referente: richiesta.nome_referente || "",
    cognome_referente: richiesta.cognome_referente || "",
    telefono: richiesta.telefono || "",
    consenso_privacy: true,
    email_referente: richiesta.email_referente || "",
    relazione_ospite: richiesta.relazione_ospite || "",
    nome_ospite: richiesta.nome_ospite || "",
    eta_ospite: richiesta.eta_ospite || "",
    sesso_ospite: richiesta.sesso_ospite || "",
    autosufficienza: richiesta.autosufficienza || "",
    urgenza_inserimento: richiesta.urgenza_inserimento || "",
    tipo_posto: richiesta.tipo_posto || "",
    patologie: richiesta.patologie || "",
    fonte: richiesta.fonte || "",
    budget: richiesta.budget || "",
    zona: richiesta.zona || "",
    note: richiesta.note || "",
  };
}

export default function NuovaRichiestaDialog({ open, onOpenChange, onCreated, richiesta, onUpdated }) {
  const { token } = useAuth();
  const isEdit = !!richiesta?.id;
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && richiesta) {
      setForm(richiestaToForm(richiesta));
    } else if (open && !richiesta) {
      setForm(EMPTY_FORM);
    }
  }, [open, richiesta]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleClose = (v) => {
    if (!v) setForm(EMPTY_FORM);
    onOpenChange(v);
  };

  const buildPayload = () => {
    const { consenso_privacy, ...fields } = form;
    return Object.fromEntries(
      Object.entries(fields).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
        .filter(([, v]) => v !== "" && v !== null)
    );
  };

  const handleSubmit = async () => {
    if (!form.nome_referente.trim()) {
      toast.error("Il nome del referente è obbligatorio");
      return;
    }
    if (!form.cognome_referente.trim()) {
      toast.error("Il cognome del referente è obbligatorio");
      return;
    }
    if (!form.telefono.trim()) {
      toast.error("Il numero di telefono è obbligatorio");
      return;
    }
    if (!isEdit && !form.consenso_privacy) {
      toast.error("Devi confermare il consenso al trattamento dei dati");
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload();
      const headers = { Authorization: `Bearer ${token}` };
      const res = isEdit
        ? await axios.put(`${API_URL}/api/ospiti-rete/richieste/${richiesta.id}`, payload, { headers })
        : await axios.post(`${API_URL}/api/ospiti-rete/richieste`, { ...payload, consenso_privacy: true }, { headers });
      toast.success(isEdit ? "Dati aggiornati con successo" : "Richiesta salvata con successo");
      handleClose(false);
      if (isEdit) {
        onUpdated?.(res.data);
      } else {
        onCreated?.(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            {isEdit ? "Modifica dati richiesta" : "Nuova richiesta posto letto"}
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            {isEdit
              ? "Aggiorna referente, ospite e informazioni richiesta. Lo stato non è modificabile da qui."
              : "Inserisci solo nome, cognome e telefono. Potrai completare la scheda in qualsiasi momento."}
          </p>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Dati obbligatori */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
              Dati obbligatori
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="nome_referente">
                  Nome referente <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nome_referente"
                  value={form.nome_referente}
                  onChange={(e) => set("nome_referente", e.target.value)}
                  placeholder="Inserisci nome"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cognome_referente">
                  Cognome referente <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="cognome_referente"
                  value={form.cognome_referente}
                  onChange={(e) => set("cognome_referente", e.target.value)}
                  placeholder="Inserisci cognome"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="telefono">
                  Telefono <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="telefono"
                    type="tel"
                    className="pl-9"
                    value={form.telefono}
                    onChange={(e) => set("telefono", e.target.value)}
                    placeholder="Es. 333 201 7904"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Dati del referente */}
          <section>
            <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">
              Dati del referente (facoltativi)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="email_referente">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email_referente"
                    type="email"
                    className="pl-9"
                    value={form.email_referente}
                    onChange={(e) => set("email_referente", e.target.value)}
                    placeholder="email@esempio.it"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Relazione con l'ospite</Label>
                <Select
                  value={form.relazione_ospite}
                  onValueChange={(v) => set("relazione_ospite", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona relazione" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Figlio/a", "Coniuge", "Nipote", "Fratello/Sorella", "Altro familiare", "Tutore legale", "Medico curante", "Assistente sociale"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Dati dell'ospite */}
          <section>
            <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">
              Dati dell'ospite (facoltativi)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="nome_ospite">Nome e cognome ospite</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="nome_ospite"
                    className="pl-9"
                    value={form.nome_ospite}
                    onChange={(e) => set("nome_ospite", e.target.value)}
                    placeholder="Inserisci nome e cognome"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="eta_ospite">Età</Label>
                <Input
                  id="eta_ospite"
                  value={form.eta_ospite}
                  onChange={(e) => set("eta_ospite", e.target.value)}
                  placeholder="Es. 82"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Sesso</Label>
                <Select value={form.sesso_ospite} onValueChange={(v) => set("sesso_ospite", v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschio</SelectItem>
                    <SelectItem value="F">Femmina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Autosufficienza</Label>
                <Select value={form.autosufficienza} onValueChange={(v) => set("autosufficienza", v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {["Autosufficiente", "Parzialmente autosufficiente", "Non autosufficiente"].map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Urgenza inserimento</Label>
                <Select value={form.urgenza_inserimento} onValueChange={(v) => set("urgenza_inserimento", v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {["Urgente (entro 1 settimana)", "Entro 1 mese", "Entro 3 mesi", "Senza urgenza"].map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo posto richiesto</Label>
                <Select value={form.tipo_posto} onValueChange={(v) => set("tipo_posto", v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {["Singola", "Doppia", "RSA", "Nucleo Alzheimer", "Lungodegenza", "Centro diurno"].map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-3">
                <Label htmlFor="patologie">Patologie principali</Label>
                <Input
                  id="patologie"
                  value={form.patologie}
                  onChange={(e) => set("patologie", e.target.value)}
                  placeholder="Es. Ipertensione, Diabete, Alzheimer"
                />
              </div>
            </div>
          </section>

          {/* Informazioni richiesta */}
          <section>
            <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">
              Informazioni richiesta (facoltativi)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Fonte richiesta</Label>
                <Select value={form.fonte} onValueChange={(v) => set("fonte", v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona fonte" /></SelectTrigger>
                  <SelectContent>
                    {["Telefono diretto", "Email", "Sito web struttura", "Casadiriposoperte.it", "Vitalma", "Passaparola", "Medico curante", "Assistente sociale", "Altra struttura", "Altro"].map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="budget">Budget indicativo</Label>
                <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona budget" /></SelectTrigger>
                  <SelectContent>
                    {["Fino a 1.500 €", "1.500 – 2.000 €", "2.000 – 2.500 €", "2.500 – 3.000 €", "Oltre 3.000 €", "Da valutare"].map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="zona">Zona richiesta</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="zona"
                    className="pl-9"
                    value={form.zona}
                    onChange={(e) => set("zona", e.target.value)}
                    placeholder="Es. Bari e provincia"
                  />
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-3">
                <Label htmlFor="note">Note</Label>
                <Textarea
                  id="note"
                  value={form.note}
                  onChange={(e) => set("note", e.target.value)}
                  placeholder="Inserisci eventuali note..."
                  rows={2}
                />
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="mt-5 pt-4 border-t border-slate-100 flex-col sm:flex-row gap-3">
          {!isEdit ? (
            <div className="flex items-start gap-2 flex-1">
              <Shield className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-slate-500">I dati sono al sicuro e protetti.</p>
                <label className="flex items-center gap-2 mt-1 cursor-pointer">
                  <Checkbox
                    checked={form.consenso_privacy}
                    onCheckedChange={(v) => set("consenso_privacy", !!v)}
                  />
                  <span className="text-xs text-slate-600">
                    Ho ricevuto il consenso al trattamento dei dati.
                  </span>
                </label>
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500 flex-1">Lo stato della richiesta si aggiorna automaticamente in base alle azioni effettuate.</p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleClose(false)} disabled={loading}>
              Annulla
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isEdit ? "Salva modifiche" : "Salva richiesta"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
